import React, { PropsWithChildren, useEffect, useRef, useState, ReactElement } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import {
    StarArray,
    getBazaarVoiceStarData,
} from '../../foundation/StarArray/StarArray';

import './PullQuote.css';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { TCreditCard } from '../../types/models/CreditCard';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { Button, Row, Col, Section, CarouselSlider } from '../../importCL';

interface ITestimonialContent {
    author?: string;
    quote?: string;
}

interface IReviewData {
    AverageOverallRating: number;
    TotalReviewCount: number;
    OverallRatingRange: number;
}

interface IPullQuoteProps extends MappedComponentProperties {
    backgroundDotsUrl?: string;
    topQuotationSvgUrl?: string;
    topQuotationPngUrl?: string;
    topQuotationAltText?: string;
    topQuotationPngVariantEnabled?: boolean;
    version?: string;
    bazaarvoiceCode?: string;
    reviewedCardName?: string;
    ctaLink?: string;
    ctaLabel?: string;
    testimonials?: ITestimonialContent[];
    card?: TCreditCard;
}

export const PullQuoteEditConfig = {
    emptyLabel: 'PullQuote',
    isEmpty: (props: PropsWithChildren<IPullQuoteProps>) => {
        return !props.testimonials || !props.ctaLabel;
    },
};

const PullQuoteSlides = (testimonials?: ITestimonialContent[]): React.ReactNode[] => {
    if (!testimonials || !testimonials.length) {
        return [];
    }

    return testimonials.map((testimonial, i) => (
        <div key={i}>
            <p className='PullQuote__reviewBody'>
                {transformHTMLParserLinks(testimonial.quote)}
            </p>

            <section className='PullQuote__reviewName'>
                {transformHTMLParserLinks(testimonial.author)}
            </section>
        </div>
    ));
};

const PullQuote = (props: PropsWithChildren<IPullQuoteProps>): JSX.Element => {
    const [data, setData] = useState<IReviewData>();
    const showRating = data;
    const testimonialSlides = PullQuoteSlides(props.testimonials);
    const quoteRef = useRef<any>([]);
    const getAverageRating = (avg: number) => (Math.round(avg * 10) / 10).toFixed(1);
    const useImage = props.topQuotationPngUrl || props.topQuotationSvgUrl;

    useEffect(() => {
        if (!props.bazaarvoiceCode) {
            return;
        }

        getBazaarVoiceStarData(props.bazaarvoiceCode)
            .then((result) => setData({
                AverageOverallRating: result.average,
                TotalReviewCount: result.total,
                OverallRatingRange: result.overall,
            }))
            .catch((err) => console.error(err));
    }, []);

    const getReviewSlider = () => {
        if (props.testimonials !== undefined) {
            return <div>
                <Section className='PullQuote__Section' bgColor='white'>
                    <CarouselSlider
                        infinite
                        properties={{
                            slideGap: 16,
                            buttonPadding: 0,
                        }}
                        customArrows= {{
                            arrowSize: 22,
                            arrowColor: '#0a5ec0',
                        }}
                        slides={getTestimonials()} />
                </Section>
            </div>;
        }
    };

    const getReviewStats = () => {
        if (data !== null && props.bazaarvoiceCode && props.version === 'cardMemberReviews') {
            return <div className='PullQuote__center PullQuote__cardText'>
                <StarArray size={28} average={data?.AverageOverallRating ?? 0} total={data?.TotalReviewCount ?? 0}
                    overall={data?.OverallRatingRange ?? 0} />
                <strong>{getAverageRating(data?.AverageOverallRating ?? 0)}</strong> based
                on <strong>{(data?.TotalReviewCount ?? 0).toLocaleString('en-US')}</strong> reviews of <strong> {props.card?.productName ? transformHTMLParserLinks(props.card.productName) : transformHTMLParserLinks(props.reviewedCardName)}</strong>
            </div>;
        }

        return <div className='PullQuote__ratingContainer'>
            <StarArray average={data?.AverageOverallRating ?? 0} total={data?.TotalReviewCount ?? 0}
                overall={data?.OverallRatingRange ?? 0} />
            <strong>{getAverageRating(data?.AverageOverallRating ?? 0)}</strong> based
            on <strong>{(data?.TotalReviewCount ?? 0).toLocaleString('en-US')}</strong> reviews of <strong> {props.card?.productName ? transformHTMLParserLinks(props.card.productName) : transformHTMLParserLinks(props.reviewedCardName)}</strong>
        </div>;
        
    };

    const getTestimonials = () => {
        if (!props.testimonials || props.testimonials.length < 1) {
            return [];
        }

        return props.testimonials.map((testimony, index) =>
            <div className='PullQuote__contentContainer' key={index}>
                <p ref={el => quoteRef.current[index] = el} className='PullQuote__quote'>
                    {transformHTMLParserLinks(testimony.quote)}
                </p>
                <section className='PullQuote__author'>
                    {'- ' + transformHTMLParserLinks(testimony.author)}
                </section>
            </div>,
        );
    };


    return (
        <>
            { props.version === 'pullQuote' && (
                <div id='pull-quote' className='PullQuote'>
                    <div className='PullQuote__curvedTopContainer'>
                        <section className='PullQuote__curvedTop' />
                    </div>
                    <Section containerClass='PullQuote__reviewContainer' className={`PullQuote__reviewSection ${useImage ? '':'PullQuote__reviewSection--noImage'}`} bgColor='blue-denim' aria-label='carousel testimonials'>
                        {useImage && (
                            <div className={`PullQuote__topQuotationContainer${showRating ? '--padded' : ''}`}>
                                <img
                                    className='PullQuote__topQuotation'
                                    loading='lazy'
                                    alt={props.topQuotationAltText}
                                    src={props.topQuotationPngVariantEnabled ? props.topQuotationPngUrl : props.topQuotationSvgUrl}
                                />
                            </div>
                        )}

                        {data && (
                            getReviewStats()
                        )}
                        {testimonialSlides?.length > 0 && (
                            <CarouselSlider 
                                infinite
                                properties={{ buttonPadding: 0 }}
                                customArrows= {{
                                    arrowSize: 30,
                                    arrowColor: '#fff',
                                }}
                                slides={testimonialSlides}/>
                        )}
                        {
                            props.ctaLabel && (
                                <div className='PullQuote__reviewButtonContainer'>
                                    <Button
                                        className='PullQuote__reviewButton'
                                        variant='link'
                                        size='medium'
                                        icon='chevron'
                                        href={props.ctaLink}
                                    >
                                        {transformHTMLParserLinks(props.ctaLabel) as unknown as ReactElement}
                                    </Button>
                                </div>
                            )
                        }

                        {
                            props.backgroundDotsUrl && (
                                <div className='PullQuote__dotsContainer'>
                                    <img
                                        className='PullQuote__dots'
                                        src={props.backgroundDotsUrl}
                                        loading='lazy'
                                        alt='dots'
                                    />
                                </div>
                            )
                        }
                    </Section >
                </div >
            )}
            {props.version === 'cardMemberReviews' && (
                <div id='card-member-reviews' className='PullQuote'>
                    <Section className='PullQuote__heading'>
                        <div className='PullQuote__review'>
                            {getReviewStats()}
                        </div>
                    </Section>
                    <div>
                        {getReviewSlider()}
                    </div>
                    <Row>
                        <Col className='PullQuote__button'>
                            <LinkWrapper className='PullQuote__link' href={props.ctaLink}>
                                <Button size='medium' variant='ghosted-dark'>{transformHTMLParserLinks(props.ctaLabel) as unknown as ReactElement}</Button>
                            </LinkWrapper>
                        </Col>
                    </Row>
                </div>
            )}
        </>
    );
};

export default PullQuote;